/**
 * PurchaseOrderApprovalSettingsSection Component
 *
 * Purchase order approval drawn as its flow — the AI decides first (switch, editable policy,
 * dry-run), the chain and the fallback recipient name a user when it does not. Only the policy
 * needs a save; switches and the chain are stored the moment they change.
 */
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertBanner, ConfirmationModal, Loader } from '@insio/ui-toolkit';
import { ApprovalType, AutomationKind, Permission } from '@insio/shared-types';
import {
  PreviewPurchaseOrderApprovalDocument,
  PurchaseOrderApprovalSettingsDocument,
  ResetPurchaseOrderApprovalSettingsDocument,
  UpdatePurchaseOrderApprovalSettingsDocument
} from '@/graphql';
import { navigation } from '@/navigation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { AutomationHelpModal } from './AutomationHelpModal';
import { ApprovalPreviewResult } from './ApprovalPreviewResult';
import { ApprovalFlowSection } from './approval-flow/ApprovalFlowSection';
import { usersOutsideOrgStructure } from './approval-flow/approval-flow';
import { useApprovalSettingsForm } from './approval-flow/use-approval-settings-form';
import { PurchaseOrderPickerModal } from './PurchaseOrderPickerModal';
import type { LifecycleStepRef } from './types';

const TRANSLATION_PREFIX = 'systemSettings.automation.purchaseOrderApproval';

export interface PurchaseOrderApprovalSettingsSectionProps {
  /** Opens another lifecycle step; the skipped notice links the next one. */
  onOpenStep?: (step: LifecycleStepRef) => void;
}

export function PurchaseOrderApprovalSettingsSection({
  onOpenStep
}: PurchaseOrderApprovalSettingsSectionProps) {
  const t = useFeatureTranslation(TRANSLATION_PREFIX);
  const form = useApprovalSettingsForm({
    settingsQuery: PurchaseOrderApprovalSettingsDocument,
    selectSettings: data => data.purchaseOrderApprovalSettings,
    updateMutation: UpdatePurchaseOrderApprovalSettingsDocument,
    resetMutation: ResetPurchaseOrderApprovalSettingsDocument,
    selectReset: data => data.resetPurchaseOrderApprovalSettings,
    approvalType: ApprovalType.PURCHASE_ORDER_APPROVAL,
    approvePermission: Permission.PURCHASE_ORDERS_APPROVE,
    translationPrefix: TRANSLATION_PREFIX
  });
  const { settings, loading, error, users, flowProps } = form;
  const [runPreview, { data: previewData, loading: isPreviewing, error: previewError }] =
    useMutation(PreviewPurchaseOrderApprovalDocument);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isResultDismissed, setIsResultDismissed] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // Only the approver candidates are known here, so only they are listed.
  const usersWithoutUnit = usersOutsideOrgStructure(users, flowProps.fallback.strategy);

  const handleOpenPreview = () => {
    if (flowProps.isReadOnly) return;
    setIsPickerOpen(true);
  };

  const handlePreview = async (purchaseOrderId: string) => {
    // The dry-run needs rules.manage, like every change of the rules.
    if (flowProps.isReadOnly) return;
    setIsResultDismissed(false);
    try {
      await runPreview({ variables: { purchaseOrderId } });
    } catch {
      // Surfaced by the result panel via previewError — the mutation error is the single source.
    }
  };

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  const preview = previewData?.previewPurchaseOrderApproval;
  const previewFootnote = preview
    ? preview.approverProposal === 'PROPOSED'
      ? t('preview.proposedApprover', {
          name: preview.proposedApproverName ?? t('preview.unknownApprover')
        })
      : preview.approverProposal === 'NO_SUITABLE_CANDIDATE'
        ? t('preview.noSuitableCandidate')
        : null
    : null;

  return (
    <>
      <ApprovalFlowSection
        document='purchaseOrder'
        translationPrefix={TRANSLATION_PREFIX}
        testIdPrefix='purchase-order-approval'
        {...flowProps}
        isPreviewing={isPreviewing}
        onOpenPreview={handleOpenPreview}
        onOpenHelp={() => setIsHelpOpen(true)}
        previewResult={
          !isResultDismissed && (
            <ApprovalPreviewResult
              isRunning={isPreviewing}
              hasError={previewError != null}
              result={preview}
              onDismiss={() => setIsResultDismissed(true)}
              footnote={previewFootnote}
              translationPrefix='systemSettings.automation.purchaseOrderApproval.preview'
              statusEnum='requisitionApprovalStatus'
            />
          )
        }
        notice={
          usersWithoutUnit.length > 0 && (
            <AlertBanner
              variant='warning'
              title={t('orgStructureWarning.title')}
              body={
                <>
                  {t('orgStructureWarning.message')} {t('orgStructureWarning.usersLabel')}{' '}
                  {usersWithoutUnit.map((user, index) => (
                    <span key={user.id}>
                      {index > 0 && ', '}
                      <Link
                        to={navigation.codeLists('org-structure')}
                        className='underline hover:opacity-70'
                        data-testid={`purchase-order-approval-unplaced-${user.id}`}
                      >
                        {user.displayName}
                      </Link>
                    </span>
                  ))}
                </>
              }
              testId='purchase-order-approval-org-structure-warning'
            />
          )
        }
        onOpenStep={onOpenStep}
      />

      <ConfirmationModal
        open={form.isResetConfirmOpen}
        title={t('resetConfirm.title')}
        message={t('resetConfirm.message')}
        confirmLabel={t('resetConfirm.confirm')}
        cancelLabel={t('resetConfirm.cancel')}
        variant='warning'
        loading={form.isResetting}
        onConfirm={form.handleReset}
        onClose={form.closeResetConfirm}
        testId='purchase-order-approval-reset-confirm'
      />

      <AutomationHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        kind={AutomationKind.PURCHASE_ORDER_APPROVAL}
        title={t('title')}
      />

      <PurchaseOrderPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handlePreview}
        title={t('preview.title')}
        description={t('preview.description')}
        testId='purchase-order-approval-preview-picker'
      />
    </>
  );
}
